import * as React from 'react';
import { Icon } from '../Icon/Icon';
import type { CameraImageProps } from './Cameras.types';

interface CameraImageState {
    time: Date;
    isLoaded: boolean;
    imageSource: string;
    fullSizeImageSource?: string;
}

export class CameraImage extends React.PureComponent<CameraImageProps, CameraImageState> {
    _timer: ReturnType<typeof setTimeout>;
    imageRef: React.RefObject<HTMLImageElement>;
    observer: IntersectionObserver;

    constructor(props: CameraImageProps) {
        super(props);

        this.imageRef = React.createRef();
        this.state = {
            time: new Date(),
            isLoaded: false,
            imageSource: null,
            fullSizeImageSource: null,
        };
    }

    public componentDidMount(): void {
        if (
            !('IntersectionObserver' in window) ||
            !('IntersectionObserverEntry' in window) ||
            !('intersectionRatio' in window.IntersectionObserverEntry.prototype) ||
            !('isIntersecting' in window.IntersectionObserverEntry.prototype)
        ) {
            this.loadCameraImage();
        } else {
            this.observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    const { isIntersecting } = entry;
                    if (isIntersecting) {
                        this.loadCameraImage();
                        this.observer.disconnect();
                    }
                });
            });
            this.observer.observe(this.imageRef.current);
        }
    }

    componentWillUnmount(): void {
        document.removeEventListener('visibilitychange', this._handleVisibilityChange);
        this._stopUpdates();

        if (this.state.imageSource?.startsWith('blob:')) {
            URL.revokeObjectURL(this.state.imageSource);
        }
        if (this.state.fullSizeImageSource?.startsWith('blob:')) {
            URL.revokeObjectURL(this.state.fullSizeImageSource);
        }
    }

    _scheduleUpdate() {
        if (!this._timer) {
            this._timer = setTimeout(() => {
                this._timer = null;
                this.updateCameraImage();
            }, this.props.updateTime || 10000);
        }
    }

    _stopUpdates() {
        clearTimeout(this._timer);
        this._timer = null;
    }

    _handleVisibilityChange = () => {
        if (document.hidden) {
            this._stopUpdates();
        } else {
            this.updateCameraImage();
        }
    };

    componentDidUpdate(): void {
        if (this.props.onUpdate) {
            this.props.onUpdate(this.state.time);
        }
    }

    loadCameraImage(): void {
        if (!this.state.isLoaded) {
            this.updateCameraImage();
        }
        if (this.props.imageUrl) {
            this.updateCameraImage();
            document.addEventListener('visibilitychange', this._handleVisibilityChange);
        }
    }

    updateCameraImage = (): void => {
        const fetchTime = new Date();
        this.getImage(this.props.imageUrl)
            .then((blob) => {
                // Revoke old blob URL before creating new one
                if (this.state.imageSource && this.state.imageSource.startsWith('blob:')) {
                    URL.revokeObjectURL(this.state.imageSource);
                }

                const url = blob ? window.URL.createObjectURL(blob) : this.props.noImageUrl || '/images/camera-no-image.jpg';
                this.setState({
                    time: fetchTime,
                    imageSource: url,
                    isLoaded: true,
                });
            })
            .finally(() => {
                this._scheduleUpdate();
            });
        if (this.props.hasFullSizePhoto && this.state.fullSizeImageSource) {
            const fullSizeUrl = this.getFullSizeImageUrl();
            this.getImage(fullSizeUrl).then((blob) => {
                if (blob) {
                    const oldFullSizeUrl = this.state.fullSizeImageSource;
                    const newUrl = window.URL.createObjectURL(blob);

                    this.setState(
                        {
                            fullSizeImageSource: newUrl,
                        },
                        () => {
                            // Revoke old blob URL only after state is updated
                            if (oldFullSizeUrl && oldFullSizeUrl.startsWith('blob:')) {
                                URL.revokeObjectURL(oldFullSizeUrl);
                            }
                        },
                    );
                }
            });
        }
    };

    handleExpandClick = async (e: React.MouseEvent): Promise<void> => {
        e.preventDefault();

        if (!this.state.fullSizeImageSource && this.props.hasFullSizePhoto) {
            const fullSizeUrl = this.getFullSizeImageUrl();
            const blob = await this.getImage(fullSizeUrl);
            if (blob) {
                const url = window.URL.createObjectURL(blob);
                this.setState({
                    fullSizeImageSource: url,
                });
            }
        }
    };

    getFullSizeImageUrl(): string {
        if (!this.props.imageUrl) return '';
        const separator = this.props.imageUrl.includes('?') ? '&' : '?';
        return `${this.props.imageUrl}${separator}type=fullsize`;
    }

    public render(): JSX.Element {
        const shouldShowExpandButton = this.props.hasFullSizePhoto && this.props.showExpandButton && this.props.imageUrl;
        const modalId = shouldShowExpandButton ? `camera-modal-${this.props.id}` : null;

        return (
            <>
                <div
                    className={shouldShowExpandButton ? 'camera-image__wrapper--with-expand' : undefined}
                    style={{ position: shouldShowExpandButton ? 'relative' : undefined }}
                >
                    <img
                        ref={this.imageRef}
                        alt={this.props.title ?? ''}
                        title={this.props.title}
                        src={this.state.imageSource}
                        onError={this.onError}
                    />
                    {shouldShowExpandButton && (
                        <button
                            type="button"
                            className="position-absolute camera-image__expand-button p-1"
                            data-bs-toggle="modal"
                            data-bs-target={`#${modalId}`}
                            onClick={this.handleExpandClick}
                        >
                            <Icon className="camera-image__expand-button-icon" symbol="icon-expand" />
                            <span className="visually-hidden">Förstora bild</span>
                        </button>
                    )}
                </div>

                {shouldShowExpandButton && (
                    <div className="modal fade camera-modal" id={modalId} tabIndex={-1} role="dialog" aria-hidden="true">
                        <div className="modal-dialog camera-modal__dialog">
                            <div className="modal-content camera-modal__content">
                                <div className="modal-body camera-modal__body">
                                    <div className="position-relative camera-modal__image-wrapper">
                                        {this.state.fullSizeImageSource && <img src={this.state.fullSizeImageSource} alt={this.props.title} />}
                                        <button
                                            type="button"
                                            className="close camera-modal__close-btn position-absolute border-0"
                                            data-bs-dismiss="modal"
                                            aria-label="Stäng"
                                        >
                                            <span aria-hidden="true">&times;</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    private getImage(url: string): Promise<Blob> {
        return fetch(url, {
            cache: this.state.isLoaded ? 'default' : 'reload',
        })
            .then((response) => {
                if (response.ok && response.status === 200) {
                    return response.blob();
                }
                return null;
            })
            .catch(() => {
                return null;
            });
    }

    private onError = () => {
        this.setState({
            imageSource: this.props.noImageUrl || '/images/camera-no-image.jpg',
        });
    };
}
